import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Chip, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, IconButton, Collapse, Stack, Divider, Dialog, DialogTitle,
  DialogContent, DialogActions, LinearProgress, Tooltip, InputAdornment,
  Tabs, Tab, Badge,
} from '@mui/material';
import {
  Search as SearchIcon, Delete as DeleteIcon,
  ExpandMore as ExpandIcon, ExpandLess as CollapseIcon,
  Refresh as RefreshIcon, CloudUpload as ImportIcon,
  Visibility as PublicIcon, VisibilityOff as PrivateIcon,
  Error as ErrorIcon, Link as LinkIcon,
  Send as SendIcon, SmartToy as AiIcon, Save as SaveIcon,
  Edit as EditIcon, Timer as TimerIcon,
  PhoneIphone as MobileIcon, Restaurant as RestaurantIcon,
  LocalFireDepartment as CaloriesIcon,
  AddPhotoAlternate as AddPhotoIcon, Star as StarIcon,
  DeleteOutline as DeleteMediaIcon, Image as ImageIcon,
  AutoAwesome as AIImageIcon,
  CheckCircle as CheckIcon,
  Science as ScienceIcon, Close as CloseIcon,
} from '@mui/icons-material';
import { adminApi } from '../services/adminApi';

const AUTHOR_USER_ID = 'f6941e38-9854-4d58-98a4-140390438637'; // brandon+test@famelia.app (Famēlia family)

interface SearchResult {
  title: string;
  url: string;
  description: string;
  source: string;
  displayUrl: string;
  rating?: number;
  reviewCount?: number;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  stats?: { urls_found?: number; scraped?: number; nutrition_analyzed?: number; imported?: number; failed?: number; queries_run?: number };
  imported?: Array<{ id: string; title: string; nutrition_summary: any; ai_review: string; source_url: string }>;
  searchResults?: SearchResult[];
}

interface ScrapedRecipe {
  url: string;
  success: boolean;
  error?: string;
  recipe?: {
    title: string;
    description?: string;
    ingredients: string[];
    instructions: string[];
    prepTime?: string;
    cookTime?: string;
    servings?: string | number;
    image?: string;
    sourceUrl: string;
    author?: string;
  };
}

interface ImportedRecipe {
  id: string;
  title: string;
  description: string | null;
  cuisine_type: string | null;
  prep_time: number;
  cook_time: number;
  servings: number;
  source_type: string;
  source_url: string | null;
  source_favicon_url: string | null;
  is_public: boolean;
  processing_status: string;
  nutrition_summary: any;
  nutrition_status: string;
  tags: string[];
  tag_names?: string[];
  created_at: string;
  user_id: string;
}

const PROGRESS_STEPS = [
  'Understanding your request...',
  'Searching popular recipe websites...',
  'Collecting results...',
  'Almost done...',
];

export function RecipeManagerTab() {
  // ── Chat state ──
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatProgress, setChatProgress] = useState(0);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [selectedSearchResults, setSelectedSearchResults] = useState<Set<string>>(new Set());
  const [importingSearch, setImportingSearch] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState<string | null>(null);
  const [generatingImage, setGeneratingImage] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<{ recipeId: string; url: string; blobPath: string; sourceImageUrl?: string } | null>(null);
  const [savingImage, setSavingImage] = useState(false);
  const [extractingTimers, setExtractingTimers] = useState<string | null>(null);

  // ── Scraping state ──
  const [urlInput, setUrlInput] = useState('');
  const [scraping, setScraping] = useState(false);
  const [scrapedResults, setScrapedResults] = useState<ScrapedRecipe[]>([]);
  const [selectedScrapes, setSelectedScrapes] = useState<Set<number>>(new Set());
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState('');

  // ── Review queue state ──
  const [recipes, setRecipes] = useState<ImportedRecipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [selectedRecipes, setSelectedRecipes] = useState<Set<string>>(new Set());
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'private' | 'public'>('all');
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);

  // ── Editing state ──
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFields, setEditFields] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);

  // ── Delete confirmation ──
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // ── Detail cache for expanded rows ──
  const [detailCache, setDetailCache] = useState<Record<string, any>>({});
  const [loadingDetail, setLoadingDetail] = useState<string | null>(null);

  // ── Mobile preview modal ──
  const [mobilePreview, setMobilePreview] = useState<any>(null);

  // ── Ingredient nutrition lookup ──
  const [nutritionDialog, setNutritionDialog] = useState<{
    open: boolean;
    ingredient: string;
    loading: boolean;
    data: any | null;
    error: string | null;
  }>({ open: false, ingredient: '', loading: false, data: null, error: null });

  const lookupIngredientNutrition = useCallback(async (ingredientText: string) => {
    setNutritionDialog({ open: true, ingredient: ingredientText, loading: true, data: null, error: null });
    try {
      const res = await adminApi.getIngredientNutrition(ingredientText);
      if (res.success) {
        setNutritionDialog(prev => ({ ...prev, loading: false, data: res }));
      } else {
        setNutritionDialog(prev => ({ ...prev, loading: false, error: res.error || 'Not found in Edamam' }));
      }
    } catch (err: any) {
      setNutritionDialog(prev => ({ ...prev, loading: false, error: err.message || 'Lookup failed' }));
    }
  }, []);

  // ── Auto-scroll chat ──
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatProgress]);

  // ── Fetch recipes ──
  const fetchRecipes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { page, limit: 50 };
      if (filter === 'public') params.is_public = 'true';
      if (filter === 'private') params.is_public = 'false';
      const res = await adminApi.getRecipes(params);
      setRecipes(res.data || []);
      setTotalCount(res.count || 0);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load recipes');
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  useEffect(() => { fetchRecipes(); }, [fetchRecipes]);

  // ── Simulated progress during chat request ──
  useEffect(() => {
    if (!chatLoading) { setChatProgress(0); return; }
    setChatProgress(0);
    const intervals = [2000, 8000, 15000, 10000];
    let step = 0;
    let timer: ReturnType<typeof setTimeout>;
    const advance = () => {
      if (step < PROGRESS_STEPS.length - 1) {
        step++;
        setChatProgress(step);
        timer = setTimeout(advance, intervals[step] || 10000);
      }
    };
    timer = setTimeout(advance, intervals[0]);
    return () => clearTimeout(timer);
  }, [chatLoading]);

  // ── Chat send ──
  const handleChatSend = async () => {
    const msg = chatInput.trim();
    if (!msg || chatLoading) return;

    const userMsg: ChatMessage = { id: Date.now().toString(), role: 'user', content: msg, timestamp: new Date() };
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setChatLoading(true);

    try {
      const res = await adminApi.chatRecipes(msg);
      const assistantMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: res.message || 'Done.',
        timestamp: new Date(),
        stats: res.stats,
        imported: res.imported,
        searchResults: res.type === 'search_results' ? res.results : undefined,
      };
      setChatMessages(prev => [...prev, assistantMsg]);
      if (res.type === 'search_results' && res.results?.length > 0) {
        setSelectedSearchResults(new Set(res.results.map((r: SearchResult) => r.url)));
      }
      if (res.imported?.length > 0) fetchRecipes();
    } catch (err: any) {
      const errMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `Error: ${err.response?.data?.error || err.message || 'Request failed. The operation may have timed out for large batches.'}`,
        timestamp: new Date(),
      };
      setChatMessages(prev => [...prev, errMsg]);
    } finally {
      setChatLoading(false);
    }
  };

  // ── Import selected search results ──
  const handleImportSelected = async () => {
    const urls = Array.from(selectedSearchResults);
    if (urls.length === 0) return;
    setImportingSearch(true);
    try {
      const scrapeRes = await adminApi.searchRecipes(urls);
      const successfulRecipes = (scrapeRes.results || [])
        .filter((r: any) => r.success && r.recipe)
        .map((r: any) => r.recipe);

      if (successfulRecipes.length === 0) {
        const errMsg: ChatMessage = {
          id: Date.now().toString(), role: 'assistant', timestamp: new Date(),
          content: `Could not extract recipe data from the selected URLs. The sites may be blocking automated access. Try fewer selections or paste URLs directly in the import section.`,
        };
        setChatMessages(prev => [...prev, errMsg]);
        return;
      }

      const importRes = await adminApi.importRecipes(successfulRecipes, AUTHOR_USER_ID);
      const successMsg: ChatMessage = {
        id: Date.now().toString(), role: 'assistant', timestamp: new Date(),
        content: `Successfully imported ${importRes.importedCount} recipe${importRes.importedCount !== 1 ? 's' : ''}!${importRes.errorCount > 0 ? ` (${importRes.errorCount} failed)` : ''}`,
        imported: importRes.imported?.map((r: any) => ({
          id: r.id, title: r.title, nutrition_summary: r.nutrition_summary,
          ai_review: r.ai_review || r.description || '', source_url: r.source_url,
        })),
      };
      setChatMessages(prev => [...prev, successMsg]);
      setSelectedSearchResults(new Set());
      fetchRecipes();
    } catch (err: any) {
      const errMsg: ChatMessage = {
        id: Date.now().toString(), role: 'assistant', timestamp: new Date(),
        content: `Import error: ${err.response?.data?.error || err.message}`,
      };
      setChatMessages(prev => [...prev, errMsg]);
    } finally {
      setImportingSearch(false);
    }
  };

  const toggleSearchResult = (url: string) => {
    setSelectedSearchResults(prev => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url); else next.add(url);
      return next;
    });
  };

  // ── Scrape URLs ──
  const handleScrape = async () => {
    const urls = urlInput.split(/[\n,]+/).map(u => u.trim()).filter(u => u.startsWith('http'));
    if (urls.length === 0) return;
    setScraping(true);
    setScrapedResults([]);
    setSelectedScrapes(new Set());
    setError(null);
    try {
      const res = await adminApi.searchRecipes(urls);
      setScrapedResults(res.results || []);
      const s = new Set<number>();
      (res.results || []).forEach((r: any, i: number) => { if (r.success) s.add(i); });
      setSelectedScrapes(s);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Scraping failed');
    } finally {
      setScraping(false);
    }
  };

  const handleImport = async () => {
    const toImport = Array.from(selectedScrapes).map(i => scrapedResults[i]).filter(r => r.success && r.recipe).map(r => r.recipe);
    if (toImport.length === 0) return;
    setImporting(true);
    setImportProgress(`Importing ${toImport.length} recipes...`);
    try {
      const res = await adminApi.importRecipes(toImport, AUTHOR_USER_ID);
      setSuccessMsg(`Imported ${res.importedCount} recipes${res.errorCount > 0 ? ` (${res.errorCount} failed)` : ''}`);
      setScrapedResults([]);
      setSelectedScrapes(new Set());
      setUrlInput('');
      fetchRecipes();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Import failed');
    } finally {
      setImporting(false);
      setImportProgress('');
    }
  };

  const handlePublish = async (isPublic: boolean) => {
    const ids = Array.from(selectedRecipes);
    if (ids.length === 0) return;
    try {
      await adminApi.publishRecipes(ids, isPublic);
      setSuccessMsg(`${isPublic ? 'Published' : 'Unpublished'} ${ids.length} recipes`);
      setSelectedRecipes(new Set());
      fetchRecipes();
    } catch (err: any) { setError(err.response?.data?.error || err.message); }
  };

  const handleDelete = async () => {
    const ids = Array.from(selectedRecipes);
    if (ids.length === 0) return;
    try {
      await adminApi.deleteRecipes(ids);
      setSuccessMsg(`Deleted ${ids.length} recipes`);
      setSelectedRecipes(new Set());
      setDeleteDialogOpen(false);
      fetchRecipes();
    } catch (err: any) { setError(err.response?.data?.error || err.message); }
  };

  // ── Inline editing ──
  const startEditing = (recipe: ImportedRecipe) => {
    setEditingId(recipe.id);
    setEditFields({
      title: recipe.title,
      description: recipe.description || '',
      cuisine_type: recipe.cuisine_type || '',
      prep_time: recipe.prep_time,
      cook_time: recipe.cook_time,
      servings: recipe.servings,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      await adminApi.updateRecipe(editingId, editFields);
      setSuccessMsg('Recipe updated');
      setEditingId(null);
      fetchRecipes();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleMediaUpload = async (recipeId: string, file: File, isPrimary: boolean) => {
    setUploadingMedia(recipeId);
    try {
      await adminApi.uploadRecipeMedia(recipeId, file, isPrimary);
      setDetailCache(prev => { const next = { ...prev }; delete next[recipeId]; return next; });
      fetchRecipeDetail(recipeId);
      setSuccessMsg('Media uploaded');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploadingMedia(null);
    }
  };

  const handleMediaDelete = async (recipeId: string, mediaAssetId: string) => {
    try {
      await adminApi.deleteRecipeMedia(mediaAssetId);
      setDetailCache(prev => { const next = { ...prev }; delete next[recipeId]; return next; });
      fetchRecipeDetail(recipeId);
      setSuccessMsg('Media deleted');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleGenerateAIImage = async (recipeId: string) => {
    setGeneratingImage(recipeId);
    setImagePreview(null);
    try {
      const res = await adminApi.generateAIImage(recipeId);
      if (res.preview?.url) {
        setImagePreview({ recipeId, url: res.preview.url, blobPath: res.preview.blobPath, sourceImageUrl: res.preview.sourceImageUrl });
        setSuccessMsg('AI image generated — review the preview below before saving');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Image generation failed');
    } finally {
      setGeneratingImage(null);
    }
  };

  const handleSavePreviewImage = async () => {
    if (!imagePreview) return;
    setSavingImage(true);
    try {
      await adminApi.saveAIImage(imagePreview.recipeId, imagePreview.blobPath);
      setDetailCache(prev => { const next = { ...prev }; delete next[imagePreview.recipeId]; return next; });
      fetchRecipeDetail(imagePreview.recipeId);
      setImagePreview(null);
      setSuccessMsg('Image saved to recipe');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save image');
    } finally {
      setSavingImage(false);
    }
  };

  const handleDiscardPreviewImage = () => {
    setImagePreview(null);
  };

  const handleExtractTimers = async (recipeId: string) => {
    setExtractingTimers(recipeId);
    try {
      const res = await adminApi.extractTimers(recipeId);
      setDetailCache(prev => { const next = { ...prev }; delete next[recipeId]; return next; });
      fetchRecipeDetail(recipeId);
      setSuccessMsg(`Extracted ${res.timers_found || 0} timers`);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Timer extraction failed');
    } finally {
      setExtractingTimers(null);
    }
  };

  const toggleSelectAll = () => {
    if (selectedRecipes.size === recipes.length) setSelectedRecipes(new Set());
    else setSelectedRecipes(new Set(recipes.map(r => r.id)));
  };

  const toggleScrapeSelectAll = () => {
    if (selectedScrapes.size === scrapedResults.filter(r => r.success).length) setSelectedScrapes(new Set());
    else {
      const s = new Set<number>();
      scrapedResults.forEach((r, i) => { if (r.success) s.add(i); });
      setSelectedScrapes(s);
    }
  };

  const fetchRecipeDetail = useCallback(async (id: string) => {
    if (detailCache[id]) return;
    setLoadingDetail(id);
    try {
      const res = await adminApi.getRecipeDetail(id);
      if (res.success && res.recipe) {
        setDetailCache(prev => ({ ...prev, [id]: res.recipe }));
      }
    } catch (err: any) {
      console.error('Detail fetch failed:', err);
    } finally {
      setLoadingDetail(null);
    }
  }, [detailCache]);

  const formatNutrition = (ns: any) => {
    if (!ns?.per_serving) return '\u2014';
    const ps = ns.per_serving;
    const parts = [];
    if (ps.calories) parts.push(`${Math.round(ps.calories)} kcal`);
    if (ps.protein) parts.push(`${Math.round(ps.protein)}g P`);
    if (ps.carbs) parts.push(`${Math.round(ps.carbs)}g C`);
    if (ps.fat) parts.push(`${Math.round(ps.fat)}g F`);
    return parts.join(' \u00B7 ') || '\u2014';
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>}

      {/* ═══ AI CHAT SECTION ═══ */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <AiIcon color="primary" />
          <Typography variant="h6">AI Recipe Finder</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Describe what recipes you want and I'll generate them with AI, analyze nutrition via USDA, and import them as private recipes.
          Try: "Find me 15 Mediterranean dinner recipes that are high in protein and low in sodium"
        </Typography>

        {/* Chat messages */}
        {chatMessages.length > 0 && (
          <Box sx={{ maxHeight: 400, overflowY: 'auto', mb: 2, border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
            {chatMessages.map(msg => (
              <Box key={msg.id} sx={{ mb: 2, display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <Box sx={{
                  maxWidth: '85%',
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.100',
                  color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>

                  {msg.stats && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                      <Stack direction="row" spacing={2} flexWrap="wrap">
                        {msg.stats.urls_found != null && <Typography variant="caption">Found: {msg.stats.urls_found} recipes</Typography>}
                        {msg.stats.queries_run != null && <Typography variant="caption">Searches: {msg.stats.queries_run}</Typography>}
                        {msg.stats.imported != null && <Typography variant="caption" sx={{ fontWeight: 600 }}>Imported: {msg.stats.imported}</Typography>}
                        {(msg.stats.failed || 0) > 0 && <Typography variant="caption" color="error">Failed: {msg.stats.failed}</Typography>}
                      </Stack>
                    </Box>
                  )}

                  {/* Search results with selection */}
                  {msg.searchResults && msg.searchResults.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          Select recipes to import ({selectedSearchResults.size} selected):
                        </Typography>
                        <Stack direction="row" spacing={0.5}>
                          <Button size="small" variant="text" sx={{ fontSize: 11, minWidth: 0, px: 0.5 }}
                            onClick={() => setSelectedSearchResults(new Set(msg.searchResults!.map(r => r.url)))}>
                            All
                          </Button>
                          <Button size="small" variant="text" sx={{ fontSize: 11, minWidth: 0, px: 0.5 }}
                            onClick={() => setSelectedSearchResults(new Set())}>
                            None
                          </Button>
                        </Stack>
                      </Stack>
                      {msg.searchResults.map((r, i) => (
                        <Box key={r.url} onClick={() => toggleSearchResult(r.url)} sx={{
                          py: 0.8, px: 1, mb: 0.5, borderRadius: 1, cursor: 'pointer',
                          bgcolor: selectedSearchResults.has(r.url) ? 'primary.50' : 'transparent',
                          border: 1, borderColor: selectedSearchResults.has(r.url) ? 'primary.main' : 'divider',
                          '&:hover': { bgcolor: selectedSearchResults.has(r.url) ? 'primary.100' : 'grey.50' },
                          transition: 'all 0.15s',
                        }}>
                          <Stack direction="row" alignItems="flex-start" spacing={1}>
                            <Checkbox size="small" checked={selectedSearchResults.has(r.url)}
                              sx={{ p: 0, mt: 0.2 }}
                              onClick={e => e.stopPropagation()}
                              onChange={() => toggleSearchResult(r.url)} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', lineHeight: 1.3 }}>
                                {i + 1}. {r.title.replace(/ - .*$| \|.*$/, '')}
                              </Typography>
                              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                                <Chip label={r.source} size="small" variant="outlined"
                                  sx={{ height: 18, fontSize: 10, '& .MuiChip-label': { px: 0.5 } }} />
                                {r.rating != null && (
                                  <Stack direction="row" spacing={0.25} alignItems="center" sx={{ ml: 0.5 }}>
                                    <StarIcon sx={{ fontSize: 13, color: 'warning.main' }} />
                                    <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600, color: 'warning.dark' }}>
                                      {r.rating.toFixed(1)}
                                    </Typography>
                                    {r.reviewCount != null && (
                                      <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>
                                        ({r.reviewCount.toLocaleString()})
                                      </Typography>
                                    )}
                                  </Stack>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10,
                                }}>{r.description.slice(0, 100)}</Typography>
                              </Stack>
                            </Box>
                          </Stack>
                        </Box>
                      ))}
                      <Button variant="contained" size="small" fullWidth sx={{ mt: 1 }}
                        onClick={handleImportSelected}
                        disabled={selectedSearchResults.size === 0 || importingSearch}
                        startIcon={importingSearch ? <CircularProgress size={16} color="inherit" /> : <ImportIcon />}>
                        {importingSearch ? `Importing ${selectedSearchResults.size} recipes...` : `Import ${selectedSearchResults.size} Selected Recipe${selectedSearchResults.size !== 1 ? 's' : ''}`}
                      </Button>
                    </Box>
                  )}

                  {/* Imported recipes list */}
                  {msg.imported && msg.imported.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        Imported recipes:
                      </Typography>
                      {msg.imported.map((r, i) => (
                        <Box key={r.id} sx={{ py: 0.5, borderBottom: i < msg.imported!.length - 1 ? 1 : 0, borderColor: 'divider' }}>
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            {i + 1}. {r.title}
                          </Typography>
                          {r.nutrition_summary?.per_serving?.calories && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              ({formatNutrition(r.nutrition_summary)})
                            </Typography>
                          )}
                          {r.source_url && (
                            <Typography variant="caption" display="block" color="primary" component="a"
                              href={r.source_url} target="_blank" rel="noopener" sx={{ textDecoration: 'none' }}>
                              {r.source_url.replace(/https?:\/\/(www\.)?/, '').split('/').slice(0, 2).join('/')}
                            </Typography>
                          )}
                          {r.ai_review && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {r.ai_review}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}

            {chatLoading && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.100', display: 'inline-block' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">{PROGRESS_STEPS[chatProgress]}</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={((chatProgress + 1) / PROGRESS_STEPS.length) * 100} sx={{ mt: 1, borderRadius: 1 }} />
                </Box>
              </Box>
            )}
            <div ref={chatEndRef} />
          </Box>
        )}

        {/* Chat input */}
        <Stack direction="row" spacing={1}>
          <TextField
            fullWidth
            placeholder="e.g. Find diabetic friendly chicken dinner recipes..."
            value={chatInput}
            onChange={e => setChatInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSend(); } }}
            disabled={chatLoading}
            size="small"
            InputProps={{
              startAdornment: <InputAdornment position="start"><AiIcon fontSize="small" /></InputAdornment>,
            }}
          />
          <Button
            variant="contained"
            onClick={handleChatSend}
            disabled={chatLoading || !chatInput.trim()}
            startIcon={chatLoading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            sx={{ minWidth: 100 }}
          >
            {chatLoading ? 'Working...' : 'Send'}
          </Button>
        </Stack>
      </Paper>

      {/* ═══ SCRAPING SECTION ═══ */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Scrape Recipes from URLs</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Paste recipe URLs (one per line or comma-separated). Max 20 per batch.
        </Typography>
        <TextField
          fullWidth multiline minRows={3} maxRows={8}
          placeholder={"https://www.allrecipes.com/recipe/12345\nhttps://www.foodnetwork.com/recipes/..."}
          value={urlInput} onChange={e => setUrlInput(e.target.value)} disabled={scraping}
          sx={{ mb: 2 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><LinkIcon /></InputAdornment> }}
        />
        <Button
          variant="contained"
          startIcon={scraping ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
          onClick={handleScrape} disabled={scraping || !urlInput.trim()}
        >
          {scraping ? 'Scraping...' : 'Scrape URLs'}
        </Button>

        {scrapedResults.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="subtitle1">
                Scraped Results ({scrapedResults.filter(r => r.success).length}/{scrapedResults.length} successful)
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={toggleScrapeSelectAll}>
                  {selectedScrapes.size === scrapedResults.filter(r => r.success).length ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  variant="contained" color="primary"
                  startIcon={importing ? <CircularProgress size={18} color="inherit" /> : <ImportIcon />}
                  onClick={handleImport} disabled={importing || selectedScrapes.size === 0}
                >
                  {importing ? 'Importing...' : `Import ${selectedScrapes.size} Selected`}
                </Button>
              </Stack>
            </Stack>
            {importing && <LinearProgress sx={{ mb: 2 }} />}
            {importProgress && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{importProgress}</Typography>}

            {scrapedResults.map((result, idx) => (
              <Paper
                key={idx}
                variant="outlined"
                sx={{
                  mb: 2, p: 2, opacity: result.success ? 1 : 0.5,
                  borderColor: selectedScrapes.has(idx) ? 'primary.main' : 'divider',
                  borderWidth: selectedScrapes.has(idx) ? 2 : 1,
                }}
              >
                {result.success && result.recipe ? (
                  <Box>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <Checkbox
                        checked={selectedScrapes.has(idx)} size="small"
                        onChange={() => { setSelectedScrapes(prev => { const n = new Set(prev); n.has(idx) ? n.delete(idx) : n.add(idx); return n; }); }}
                      />
                      {result.recipe.image && (
                        <Box component="img" src={result.recipe.image}
                          sx={{ width: 160, height: 120, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                          onError={(e: any) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{result.recipe.title}</Typography>
                        {result.recipe.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
                            {result.recipe.description.slice(0, 200)}{result.recipe.description.length > 200 ? '...' : ''}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                          <Typography variant="caption" color="text.secondary">
                            {result.recipe.ingredients.length} ingredients
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {result.recipe.instructions.length} steps
                          </Typography>
                          {result.recipe.prepTime && <Typography variant="caption" color="text.secondary">Prep: {result.recipe.prepTime}</Typography>}
                          {result.recipe.cookTime && <Typography variant="caption" color="text.secondary">Cook: {result.recipe.cookTime}</Typography>}
                          {result.recipe.servings && <Typography variant="caption" color="text.secondary">Serves: {result.recipe.servings}</Typography>}
                        </Stack>
                        <Typography variant="caption" color="primary.main" component="a" href={result.url} target="_blank" rel="noopener" sx={{ textDecoration: 'none' }}>
                          {(() => { try { return new URL(result.url).hostname; } catch { return result.url; } })()}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Expandable ingredient + instruction preview */}
                    <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>Ingredients</Typography>
                        {result.recipe.ingredients.map((ing, i) => (
                          <Typography key={i} variant="caption" display="block" sx={{ py: 0.2 }}>
                            {ing}
                          </Typography>
                        ))}
                      </Box>
                      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>Instructions</Typography>
                        {result.recipe.instructions.map((step, i) => (
                          <Typography key={i} variant="caption" display="block" sx={{ py: 0.2 }}>
                            <strong>{i + 1}.</strong> {typeof step === 'string' ? step : (step as any)?.text || JSON.stringify(step)}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <ErrorIcon color="error" fontSize="small" />
                    <Box>
                      <Typography variant="body2" color="error">{result.error || 'Failed'}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>{result.url}</Typography>
                    </Box>
                  </Stack>
                )}
              </Paper>
            ))}
          </Box>
        )}
      </Paper>

      {/* ═══ REVIEW QUEUE ═══ */}
      <Paper sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6">Recipe Library ({totalCount})</Typography>
          <Stack direction="row" spacing={1}>
            <Chip label="All" variant={filter === 'all' ? 'filled' : 'outlined'} onClick={() => { setFilter('all'); setPage(1); }} />
            <Chip label="Private" variant={filter === 'private' ? 'filled' : 'outlined'} color="warning" onClick={() => { setFilter('private'); setPage(1); }} />
            <Chip label="Public" variant={filter === 'public' ? 'filled' : 'outlined'} color="success" onClick={() => { setFilter('public'); setPage(1); }} />
            <Tooltip title="Backfill favicons for recipes missing source icons">
              <Button size="small" variant="outlined" startIcon={<LinkIcon />}
                onClick={async () => {
                  try {
                    setError(null);
                    const res = await adminApi.backfillFavicons();
                    setSuccessMsg(`Favicons backfilled: ${res.updated} recipes updated.${res.message ? ' ' + res.message : ''}`);
                    fetchRecipes();
                  } catch (err: any) {
                    setError(err.response?.data?.error ?? err.message ?? 'Backfill failed');
                  }
                }}
                sx={{ textTransform: 'none' }}
              >Backfill Favicons</Button>
            </Tooltip>
            <IconButton onClick={fetchRecipes} size="small"><RefreshIcon /></IconButton>
          </Stack>
        </Stack>

        {selectedRecipes.size > 0 && (
          <Stack direction="row" spacing={1} sx={{ mb: 2, p: 1.5, bgcolor: 'action.selected', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ alignSelf: 'center', mr: 2 }}>{selectedRecipes.size} selected</Typography>
            <Button size="small" variant="contained" color="success" startIcon={<PublicIcon />} onClick={() => handlePublish(true)}>Publish</Button>
            <Button size="small" variant="outlined" startIcon={<PrivateIcon />} onClick={() => handlePublish(false)}>Unpublish</Button>
            <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteDialogOpen(true)}>Delete</Button>
          </Stack>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : recipes.length === 0 ? (
          <Alert severity="info">No recipes found. Use the AI chat or URL scraper above to get started!</Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedRecipes.size === recipes.length && recipes.length > 0}
                        indeterminate={selectedRecipes.size > 0 && selectedRecipes.size < recipes.length}
                        onChange={toggleSelectAll}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tags</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nutrition</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recipes.map(recipe => (
                    <React.Fragment key={recipe.id}>
                      <TableRow hover sx={{ cursor: 'pointer', '& > *': { borderBottom: expandedRow === recipe.id ? 'none' : undefined } }}>
                        <TableCell padding="checkbox">
                          <Checkbox checked={selectedRecipes.has(recipe.id)}
                            onChange={() => { setSelectedRecipes(prev => { const n = new Set(prev); n.has(recipe.id) ? n.delete(recipe.id) : n.add(recipe.id); return n; }); }}
                          />
                        </TableCell>
                        <TableCell><Typography variant="body2" sx={{ fontWeight: 500, maxWidth: 280 }} noWrap>{recipe.title}</Typography></TableCell>
                        <TableCell>
                          {recipe.tag_names && recipe.tag_names.length > 0 ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ maxWidth: 200 }}>
                              {recipe.tag_names.slice(0, 3).map((t: string) => (
                                <Chip key={t} label={t} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                              ))}
                              {recipe.tag_names.length > 3 && (
                                <Typography variant="caption" color="text.secondary">+{recipe.tag_names.length - 3}</Typography>
                              )}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">{recipe.cuisine_type || '\u2014'}</Typography>
                          )}
                        </TableCell>
                        <TableCell><Typography variant="body2" color="text.secondary">{(recipe.prep_time || 0) + (recipe.cook_time || 0)}m</Typography></TableCell>
                        <TableCell><Typography variant="caption">{formatNutrition(recipe.nutrition_summary)}</Typography></TableCell>
                        <TableCell>
                          <Chip size="small" label={recipe.is_public ? 'Public' : 'Private'}
                            color={recipe.is_public ? 'success' : 'warning'} variant="outlined"
                            icon={recipe.is_public ? <PublicIcon /> : <PrivateIcon />}
                          />
                        </TableCell>
                        <TableCell>
                          {recipe.source_url ? (
                            <Tooltip title={recipe.source_url}>
                              <Typography variant="caption" component="a" href={recipe.source_url} target="_blank" rel="noopener"
                                sx={{ color: 'primary.main', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                {recipe.source_favicon_url && (
                                  <img src={recipe.source_favicon_url} alt="" width={16} height={16} style={{ borderRadius: 2, verticalAlign: 'middle' }} />
                                )}
                                {(() => { try { return new URL(recipe.source_url).hostname; } catch { return 'Link'; } })()}
                              </Typography>
                            </Tooltip>
                          ) : '\u2014'}
                        </TableCell>
                        <TableCell><Typography variant="caption" color="text.secondary">{new Date(recipe.created_at).toLocaleDateString()}</Typography></TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => {
                            const next = expandedRow === recipe.id ? null : recipe.id;
                            setExpandedRow(next);
                            if (next) {
                              fetchRecipeDetail(recipe.id);
                              if (editingId !== recipe.id) setEditingId(null);
                            }
                          }}>
                            {expandedRow === recipe.id ? <CollapseIcon /> : <ExpandIcon />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={9} sx={{ py: 0 }}>
                          <Collapse in={expandedRow === recipe.id}>
                            <Box sx={{ py: 2, px: 1 }}>
                              {loadingDetail === recipe.id ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={24} /></Box>
                              ) : (() => {
                                const detail = detailCache[recipe.id];
                                return (
                                  <>
                                    {/* Action bar */}
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                      <Button size="small" startIcon={<EditIcon />} onClick={() => startEditing(recipe)}>Edit</Button>
                                      <Button size="small" startIcon={<MobileIcon />}
                                        onClick={() => setMobilePreview(detail || recipe)}
                                        variant="outlined"
                                      >Preview as Mobile</Button>
                                      <Divider orientation="vertical" flexItem />
                                      <Typography variant="caption" color="text.secondary">Prep: {recipe.prep_time}m</Typography>
                                      <Typography variant="caption" color="text.secondary">Cook: {recipe.cook_time}m</Typography>
                                      <Typography variant="caption" color="text.secondary">Serves: {recipe.servings}</Typography>
                                      <Chip label={recipe.nutrition_status} size="small" color={recipe.nutrition_status === 'complete' ? 'success' : 'warning'} variant="outlined" sx={{ fontSize: 10 }} />
                                    </Stack>

                                    {/* Editable fields */}
                                    {editingId === recipe.id && (
                                      <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Typography variant="subtitle2" gutterBottom>Edit Recipe</Typography>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                                          <TextField size="small" label="Title" fullWidth value={editFields.title || ''}
                                            onChange={e => setEditFields(p => ({ ...p, title: e.target.value }))} />
                                          <TextField size="small" label="Cuisine" fullWidth value={editFields.cuisine_type || ''}
                                            onChange={e => setEditFields(p => ({ ...p, cuisine_type: e.target.value }))} />
                                          <TextField size="small" label="Description" fullWidth multiline maxRows={3}
                                            value={editFields.description || ''}
                                            onChange={e => setEditFields(p => ({ ...p, description: e.target.value }))} />
                                          <Stack direction="row" spacing={1}>
                                            <TextField size="small" label="Prep (min)" type="number" value={editFields.prep_time ?? 0}
                                              onChange={e => setEditFields(p => ({ ...p, prep_time: parseInt(e.target.value) || 0 }))} />
                                            <TextField size="small" label="Cook (min)" type="number" value={editFields.cook_time ?? 0}
                                              onChange={e => setEditFields(p => ({ ...p, cook_time: parseInt(e.target.value) || 0 }))} />
                                            <TextField size="small" label="Servings" type="number" value={editFields.servings ?? 4}
                                              onChange={e => setEditFields(p => ({ ...p, servings: parseInt(e.target.value) || 4 }))} />
                                          </Stack>
                                        </Box>
                                        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                                          <Button size="small" variant="contained" startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
                                            onClick={handleSaveEdit} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
                                          <Button size="small" variant="outlined" onClick={() => setEditingId(null)}>Cancel</Button>
                                        </Stack>
                                      </Box>
                                    )}

                                    {/* Description + tags */}
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{recipe.description || 'No description'}</Typography>
                                    {detail?.tag_names && Object.keys(detail.tag_names).length > 0 && (
                                      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 2 }}>
                                        {Object.values(detail.tag_names as Record<string, string>).map((name) => (
                                          <Chip key={name} label={name} size="small" variant="outlined" />
                                        ))}
                                      </Stack>
                                    )}

                                    {/* Media Section — shown first for visibility */}
                                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                          <ImageIcon fontSize="small" color="action" />
                                          <Typography variant="subtitle2">Media ({detail?.media?.length || 0})</Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1}>
                                          <Button size="small" startIcon={uploadingMedia === recipe.id ? <CircularProgress size={14} /> : <AddPhotoIcon />}
                                            variant="outlined" component="label"
                                            disabled={uploadingMedia === recipe.id}>
                                            {uploadingMedia === recipe.id ? 'Uploading...' : 'Upload'}
                                            <input type="file" hidden accept="image/*,video/mp4"
                                              onChange={e => {
                                                const file = e.target.files?.[0];
                                                if (file) handleMediaUpload(recipe.id, file, !detail?.media?.length);
                                                e.target.value = '';
                                              }} />
                                          </Button>
                                          <Button size="small" variant="outlined" color="warning"
                                            startIcon={generatingImage === recipe.id ? <CircularProgress size={14} color="warning" /> : <AIImageIcon />}
                                            disabled={generatingImage === recipe.id || uploadingMedia === recipe.id}
                                            onClick={() => handleGenerateAIImage(recipe.id)}
                                            sx={{ borderColor: 'warning.main', color: 'warning.dark', '&:hover': { bgcolor: 'warning.50', borderColor: 'warning.dark' } }}>
                                            {generatingImage === recipe.id ? 'Generating...' : 'Generate AI Image'}
                                          </Button>
                                        </Stack>
                                      </Stack>
                                      {/* AI Image Preview */}
                                      {imagePreview && imagePreview.recipeId === recipe.id && (
                                        <Box sx={{ p: 1.5, mb: 1.5, bgcolor: 'warning.50', border: 2, borderColor: 'warning.main', borderRadius: 1 }}>
                                          <Typography variant="subtitle2" color="warning.dark" sx={{ mb: 1 }}>AI Image Preview — Review before saving</Typography>
                                          <Stack direction="row" spacing={2} alignItems="flex-start">
                                            {imagePreview.sourceImageUrl && (
                                              <Box>
                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Source Image</Typography>
                                                <Box component="img" src={imagePreview.sourceImageUrl}
                                                  sx={{ width: 180, height: 135, objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider' }}
                                                  onError={(e: any) => { (e.target as HTMLElement).style.display = 'none'; }} />
                                              </Box>
                                            )}
                                            <Box>
                                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>AI Generated</Typography>
                                              <Box component="img" src={imagePreview.url}
                                                sx={{ width: 240, height: 180, objectFit: 'cover', borderRadius: 1, border: 2, borderColor: 'warning.main' }} />
                                            </Box>
                                          </Stack>
                                          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                                            <Button size="small" variant="contained" color="success"
                                              disabled={savingImage}
                                              startIcon={savingImage ? <CircularProgress size={14} /> : <CheckIcon />}
                                              onClick={handleSavePreviewImage}>
                                              {savingImage ? 'Saving...' : 'Save to Recipe'}
                                            </Button>
                                            <Button size="small" variant="outlined" color="error"
                                              disabled={savingImage}
                                              onClick={handleDiscardPreviewImage}>
                                              Discard
                                            </Button>
                                            <Button size="small" variant="outlined" color="warning"
                                              disabled={generatingImage === recipe.id || savingImage}
                                              startIcon={generatingImage === recipe.id ? <CircularProgress size={14} /> : <AIImageIcon />}
                                              onClick={() => handleGenerateAIImage(recipe.id)}
                                              sx={{ borderColor: 'warning.main', color: 'warning.dark' }}>
                                              {generatingImage === recipe.id ? 'Regenerating...' : 'Regenerate'}
                                            </Button>
                                          </Stack>
                                        </Box>
                                      )}

                                      {detail?.media && detail.media.length > 0 ? (
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                          {detail.media.map((m: any) => (
                                            <Box key={m.id} sx={{ position: 'relative', width: 120, height: 90 }}>
                                              {m.type === 'video' ? (
                                                <Box component="video" src={m.url} sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1 }} />
                                              ) : (
                                                <Box component="img" src={m.url}
                                                  sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1, border: m.isPrimary ? 2 : 0, borderColor: 'primary.main' }}
                                                  onError={(e: any) => { e.target.src = ''; e.target.alt = 'Failed'; }} />
                                              )}
                                              {m.isPrimary && (
                                                <Box sx={{ position: 'absolute', top: 2, left: 2, bgcolor: 'rgba(255,255,255,0.85)', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                  <StarIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                                                </Box>
                                              )}
                                              <IconButton size="small"
                                                sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.85)', width: 22, height: 22 }}
                                                onClick={() => handleMediaDelete(recipe.id, m.id)} title="Delete">
                                                <DeleteMediaIcon sx={{ fontSize: 14, color: 'error.main' }} />
                                              </IconButton>
                                            </Box>
                                          ))}
                                        </Stack>
                                      ) : (
                                        <Typography variant="caption" color="text.secondary">No media uploaded. Click Upload or Generate AI Image to add a hero image.</Typography>
                                      )}
                                    </Box>

                                    {/* Cooking Timers — always shown */}
                                    <Box sx={{ p: 1.5, bgcolor: detail?.timers?.length > 0 ? 'warning.50' : 'grey.50', border: detail?.timers?.length > 0 ? 1 : 0, borderColor: 'warning.200', borderRadius: 1, mb: 2 }}>
                                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: detail?.timers?.length > 0 ? 1 : 0 }}>
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                          <TimerIcon fontSize="small" color={detail?.timers?.length > 0 ? 'warning' : 'disabled'} />
                                          <Typography variant="subtitle2">Cooking Timers ({detail?.timers?.length || 0})</Typography>
                                        </Stack>
                                        <Button size="small" variant="outlined" color="warning"
                                          startIcon={extractingTimers === recipe.id ? <CircularProgress size={14} color="warning" /> : <TimerIcon />}
                                          disabled={extractingTimers === recipe.id}
                                          onClick={() => handleExtractTimers(recipe.id)}
                                          sx={{ borderColor: 'warning.main', color: 'warning.dark', '&:hover': { bgcolor: 'warning.50', borderColor: 'warning.dark' } }}>
                                          {extractingTimers === recipe.id ? 'Extracting...' : detail?.timers?.length > 0 ? 'Re-extract' : 'Extract Timers'}
                                        </Button>
                                      </Stack>
                                      {detail?.timers?.length > 0 ? (
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                          {detail.timers.map((t: any, i: number) => {
                                            const mins = Math.round(t.duration_seconds / 60);
                                            const secs = t.duration_seconds % 60;
                                            const timeStr = mins > 0 ? `${mins}m${secs > 0 ? ` ${secs}s` : ''}` : `${t.duration_seconds}s`;
                                            return (
                                              <Chip key={i} icon={<TimerIcon />}
                                                label={`Step ${t.step_number}: ${t.timer_name} — ${timeStr}`}
                                                color="warning" variant="outlined"
                                                sx={{ fontSize: 11, '& .MuiChip-label': { fontWeight: 500 } }}
                                              />
                                            );
                                          })}
                                        </Stack>
                                      ) : (
                                        <Typography variant="caption" color="text.secondary">No cooking timers extracted for this recipe.</Typography>
                                      )}
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                                      {/* Ingredients */}
                                      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
                                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                                          <RestaurantIcon fontSize="small" color="action" />
                                          <Typography variant="subtitle2">Ingredients ({detail?.ingredients?.length || 0})</Typography>
                                        </Stack>
                                        {detail?.ingredients?.map((ing: any, i: number) => {
                                          const displayText = ing.original_text || `${ing.original_amount || ''} ${ing.original_unit || ''} ${ing.ingredient_name}`.trim();
                                          const failedList: string[] = recipe.nutrition_summary?.failed_ingredients || [];
                                          const isFailed = failedList.some((f: string) => f.toLowerCase() === displayText.toLowerCase());
                                          return (
                                            <Stack key={i} direction="row" alignItems="center" justifyContent="space-between" sx={{
                                              py: 0.3, borderBottom: '1px solid', borderColor: 'divider',
                                              ...(isFailed ? { bgcolor: 'warning.50', borderLeft: '3px solid', borderLeftColor: 'warning.main', pl: 0.5 } : {}),
                                            }}>
                                              <Typography variant="caption" sx={{ flex: 1, mr: 0.5, ...(isFailed ? { color: 'warning.dark', fontWeight: 600 } : {}) }}>
                                                {displayText}{isFailed ? ' ⚠️' : ''}
                                              </Typography>
                                              <Tooltip title="Lookup nutrition via Edamam">
                                                <IconButton
                                                  size="small"
                                                  onClick={() => lookupIngredientNutrition(displayText)}
                                                  sx={{ p: 0.3, minWidth: 0 }}
                                                >
                                                  <ScienceIcon sx={{ fontSize: 14, color: 'info.main' }} />
                                                </IconButton>
                                              </Tooltip>
                                            </Stack>
                                          );
                                        }) || <Typography variant="caption" color="text.secondary">Loading...</Typography>}
                                      </Box>

                                      {/* Instructions with timer badges */}
                                      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
                                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                                          <Typography variant="subtitle2">Instructions ({detail?.instructions?.length || 0})</Typography>
                                        </Stack>
                                        {detail?.instructions?.map((step: any, i: number) => {
                                          const timer = detail?.timers?.find((t: any) => t.step_number === step.step_number);
                                          return (
                                            <Box key={i} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                              <Typography variant="caption" display="block">
                                                <strong>{step.step_number}.</strong> {step.instruction_text}
                                              </Typography>
                                              {timer && (
                                                <Chip
                                                  icon={<TimerIcon />}
                                                  label={`${timer.timer_name} - ${Math.round(timer.duration_seconds / 60)}m`}
                                                  size="small"
                                                  color="warning"
                                                  variant="outlined"
                                                  sx={{ mt: 0.3, fontSize: 10 }}
                                                />
                                              )}
                                            </Box>
                                          );
                                        }) || <Typography variant="caption" color="text.secondary">Loading...</Typography>}
                                      </Box>

                                      {/* Nutrition grid */}
                                      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                                          <CaloriesIcon fontSize="small" color="error" />
                                          <Typography variant="subtitle2">Nutrition per Serving</Typography>
                                        </Stack>
                                        {recipe.nutrition_summary?.ingredient_coverage && (
                                          <Typography variant="caption" sx={{
                                            display: 'block', mb: 1, fontWeight: 500,
                                            color: recipe.nutrition_summary.failed_ingredients?.length > 0 ? 'warning.main' : 'success.main',
                                          }}>
                                            Ingredients matched: {recipe.nutrition_summary.ingredient_coverage}
                                          </Typography>
                                        )}
                                        {recipe.nutrition_summary?.failed_ingredients?.length > 0 && (
                                          <Alert severity="warning" sx={{ mb: 1, py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3 }}>
                                              Missing from nutrition calculation:
                                            </Typography>
                                            {recipe.nutrition_summary.failed_ingredients.map((ing: string, i: number) => (
                                              <Typography key={i} variant="caption" display="block" sx={{ pl: 1, color: 'warning.dark' }}>
                                                • {ing}
                                              </Typography>
                                            ))}
                                          </Alert>
                                        )}
                                        {recipe.nutrition_summary?.per_serving && Object.keys(recipe.nutrition_summary.per_serving).length > 0 ? (
                                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5 }}>
                                            {Object.entries(recipe.nutrition_summary.per_serving as Record<string, number>).map(([key, val]) => (
                                              val ? (
                                                <Box key={key} sx={{ textAlign: 'center', p: 0.5, bgcolor: 'white', borderRadius: 0.5 }}>
                                                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13 }}>
                                                    {typeof val === 'number' ? Math.round(val) : val}
                                                  </Typography>
                                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                                                    {key.replace(/([A-Z])/g, ' $1').trim()}
                                                  </Typography>
                                                </Box>
                                              ) : null
                                            ))}
                                          </Box>
                                        ) : (
                                          <Typography variant="caption" color="text.secondary">No nutrition data</Typography>
                                        )}
                                        {recipe.nutrition_summary?.dietLabels?.length > 0 && (
                                          <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap">
                                            {recipe.nutrition_summary.dietLabels.map((l: string) => <Chip key={l} label={l} size="small" color="info" variant="outlined" sx={{ fontSize: 10 }} />)}
                                          </Stack>
                                        )}
                                        {recipe.nutrition_summary?.healthLabels?.length > 0 && (
                                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                                            {recipe.nutrition_summary.healthLabels.slice(0, 8).map((l: string) => <Chip key={l} label={l} size="small" variant="outlined" sx={{ fontSize: 10 }} />)}
                                            {recipe.nutrition_summary.healthLabels.length > 8 && (
                                              <Typography variant="caption" color="text.secondary">+{recipe.nutrition_summary.healthLabels.length - 8} more</Typography>
                                            )}
                                          </Stack>
                                        )}
                                      </Box>
                                    </Box>
                                  </>
                                );
                              })()}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {(page - 1) * 50 + 1}–{Math.min(page * 50, totalCount)} of {totalCount}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <Button size="small" disabled={page * 50 >= totalCount} onClick={() => setPage(p => p + 1)}>Next</Button>
              </Stack>
            </Stack>
          </>
        )}
      </Paper>

      {/* ═══ MOBILE PREVIEW MODAL ═══ */}
      <Dialog open={!!mobilePreview} onClose={() => setMobilePreview(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MobileIcon /> Mobile Preview
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {mobilePreview && (
            <Box sx={{ bgcolor: '#f5f5f5', p: 0 }}>
              {/* Phone frame */}
              <Box sx={{
                maxWidth: 375, mx: 'auto', bgcolor: 'white', borderRadius: 3,
                overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              }}>
                {/* Hero image */}
                {(mobilePreview.hero_image || mobilePreview.image) && (
                  <Box component="img"
                    src={mobilePreview.hero_image || mobilePreview.image}
                    sx={{ width: '100%', height: 220, objectFit: 'cover' }}
                    onError={(e: any) => { e.target.style.display = 'none'; }}
                  />
                )}
                <Box sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18, mb: 0.5 }}>
                    {mobilePreview.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    by @famelia_kitchen
                  </Typography>
                  {mobilePreview.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {mobilePreview.description}
                    </Typography>
                  )}

                  {/* Macro pills */}
                  {mobilePreview.nutrition_summary?.per_serving && (
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                      {[
                        { label: 'Cal', value: mobilePreview.nutrition_summary.per_serving.calories, color: '#FF6B6B' },
                        { label: 'Protein', value: mobilePreview.nutrition_summary.per_serving.protein, unit: 'g', color: '#4ECDC4' },
                        { label: 'Carbs', value: mobilePreview.nutrition_summary.per_serving.carbs, unit: 'g', color: '#FFD93D' },
                        { label: 'Fat', value: mobilePreview.nutrition_summary.per_serving.fat, unit: 'g', color: '#6BCB77' },
                      ].filter(m => m.value).map(m => (
                        <Box key={m.label} sx={{ textAlign: 'center', flex: 1, p: 1, bgcolor: m.color + '15', borderRadius: 2 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 16, color: m.color }}>
                            {Math.round(m.value)}{m.unit || ''}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: 10 }}>{m.label}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}

                  {/* Time + servings row */}
                  <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    {(mobilePreview.prep_time > 0 || mobilePreview.prepTime) && (
                      <Chip size="small" label={`Prep: ${mobilePreview.prep_time || mobilePreview.prepTime}m`} variant="outlined" />
                    )}
                    {(mobilePreview.cook_time > 0 || mobilePreview.cookTime) && (
                      <Chip size="small" label={`Cook: ${mobilePreview.cook_time || mobilePreview.cookTime}m`} variant="outlined" />
                    )}
                    <Chip size="small" label={`Serves ${mobilePreview.servings}`} variant="outlined" />
                  </Stack>

                  <Divider sx={{ mb: 1.5 }} />

                  {/* Ingredients */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Ingredients</Typography>
                  {(mobilePreview.ingredients || []).slice(0, 10).map((ing: any, i: number) => (
                    <Stack key={i} direction="row" spacing={1} sx={{ py: 0.3 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', mt: 0.8, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ fontSize: 13 }}>
                        {typeof ing === 'string' ? ing : (ing.original_text || `${ing.original_amount || ''} ${ing.original_unit || ''} ${ing.ingredient_name}`.trim())}
                      </Typography>
                    </Stack>
                  ))}
                  {(mobilePreview.ingredients || []).length > 10 && (
                    <Typography variant="caption" color="text.secondary">+{mobilePreview.ingredients.length - 10} more...</Typography>
                  )}

                  <Divider sx={{ my: 1.5 }} />

                  {/* Instructions */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Instructions</Typography>
                  {(mobilePreview.instructions || []).slice(0, 6).map((step: any, i: number) => {
                    const text = typeof step === 'string' ? step : (step.instruction_text || step.text || '');
                    const timer = mobilePreview.timers?.find((t: any) => t.step_number === (step.step_number || i + 1));
                    return (
                      <Box key={i} sx={{ mb: 1 }}>
                        <Stack direction="row" spacing={1}>
                          <Box sx={{
                            width: 22, height: 22, borderRadius: '50%', bgcolor: 'primary.main',
                            color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 12, fontWeight: 600, flexShrink: 0, mt: 0.2,
                          }}>{step.step_number || i + 1}</Box>
                          <Typography variant="body2" sx={{ fontSize: 13 }}>{text}</Typography>
                        </Stack>
                        {timer && (
                          <Chip icon={<TimerIcon />} label={`${timer.timer_name} - ${Math.round(timer.duration_seconds / 60)}m`}
                            size="small" color="warning" sx={{ ml: 4, mt: 0.5, fontSize: 11 }} />
                        )}
                      </Box>
                    );
                  })}
                  {(mobilePreview.instructions || []).length > 6 && (
                    <Typography variant="caption" color="text.secondary">+{mobilePreview.instructions.length - 6} more steps...</Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMobilePreview(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete {selectedRecipes.size} recipes?</DialogTitle>
        <DialogContent>
          <Typography>This will permanently delete the selected recipes and all their ingredients, instructions, and media. This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Ingredient Nutrition Lookup Dialog */}
      <Dialog
        open={nutritionDialog.open}
        onClose={() => setNutritionDialog(prev => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ScienceIcon color="info" />
            <Typography variant="h6" sx={{ fontSize: 16 }}>Edamam Nutrition Lookup</Typography>
          </Stack>
          <IconButton size="small" onClick={() => setNutritionDialog(prev => ({ ...prev, open: false }))}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1, fontStyle: 'italic' }}>
            Input: &ldquo;{nutritionDialog.ingredient}&rdquo;
          </Typography>

          {nutritionDialog.loading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Looking up in Edamam...</Typography>
            </Box>
          )}

          {nutritionDialog.error && (
            <Alert severity="warning" sx={{ mb: 2 }}>{nutritionDialog.error}</Alert>
          )}

          {nutritionDialog.data && (
            <>
              {/* Edamam match info */}
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Edamam Match</Typography>
                <Stack spacing={0.3}>
                  <Typography variant="caption"><strong>Food ID:</strong> {nutritionDialog.data.edamam?.foodId}</Typography>
                  <Typography variant="caption"><strong>Matched as:</strong> {nutritionDialog.data.edamam?.foodLabel}</Typography>
                  <Typography variant="caption"><strong>Quantity:</strong> {nutritionDialog.data.edamam?.quantity} {nutritionDialog.data.edamam?.measureLabel}</Typography>
                </Stack>
              </Box>

              {/* Nutrient grid */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Nutrients ({nutritionDialog.data.nutrients?.length || 0})
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5 }}>
                {(nutritionDialog.data.nutrients || []).map((n: any) => (
                  <Box key={n.key} sx={{
                    textAlign: 'center', p: 0.8, bgcolor: n.key === 'calories' ? 'error.50' : 'grey.50',
                    borderRadius: 0.5, border: '1px solid', borderColor: n.key === 'calories' ? 'error.200' : 'grey.200',
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13 }}>
                      {n.value} {n.unit}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                      {n.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
